import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface JotformEmbedProps {
  className?: string;
  minHeight?: string;
}

const JotformEmbed = ({ className = '', minHeight = '800px' }: JotformEmbedProps) => {
  const [formId, setFormId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFormId = async () => {
      try {
        // Call test-jotform to get form ID (it validates and returns form info)
        const { data, error: fnError } = await supabase.functions.invoke('test-jotform');
        
        if (fnError) {
          console.error('Error fetching Jotform config:', fnError);
          setError('Could not load booking form');
          setLoading(false);
          return;
        }

        if (data?.success && data?.formId) {
          setFormId(data.formId);
        } else {
          setError(data?.message || 'Booking form not configured');
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Error loading booking form');
      } finally {
        setLoading(false);
      }
    };

    fetchFormId();
  }, []);

  useEffect(() => {
    if (!formId) return;

    // Load Jotform embed handler script
    const script = document.createElement('script');
    script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
    script.async = true;
    script.onload = () => {
      // Initialize the form after script loads
      if ((window as any).jotformEmbedHandler) {
        (window as any).jotformEmbedHandler(
          `iframe[id="JotFormIFrame-${formId}"]`,
          'https://form.jotform.com/'
        );
      }
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector(`script[src="${script.src}"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [formId]);

  if (loading) {
    return (
      <div 
        className={`flex items-center justify-center bg-card rounded-lg border ${className}`}
        style={{ minHeight }}
      >
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading booking form...</p>
        </div>
      </div>
    );
  }

  if (error || !formId) {
    return (
      <div 
        className={`flex items-center justify-center bg-card rounded-lg border ${className}`}
        style={{ minHeight: '200px' }}
      >
        <div className="text-center p-8">
          <p className="text-destructive font-medium mb-2">Unable to load booking form</p>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-sm text-muted-foreground mt-4">
            Please call us directly:<br />
            <a href="tel:8329303059" className="text-primary hover:underline">(832) 930-3059</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`jotform-embed-container ${className}`}>
      <iframe
        id={`JotFormIFrame-${formId}`}
        title="Booking Form"
        src={`https://form.jotform.com/${formId}`}
        style={{
          minWidth: '100%',
          maxWidth: '100%',
          height: '539px',
          border: 'none',
        }}
        scrolling="no"
        allowFullScreen
      />
      <style>{`
        .jotform-embed-container {
          width: 100%;
        }
        .jotform-embed-container iframe {
          width: 100% !important;
          border-radius: 0.5rem;
          background: transparent;
        }
        @media screen and (max-width: 480px) {
          .jotform-embed-container iframe {
            height: 100vh !important;
            min-height: 600px;
          }
        }
      `}</style>
    </div>
  );
};

export default JotformEmbed;
